import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Project, Property, Compset } from 'app/modules/projects/projects.types';

@Injectable({
  providedIn: 'root',
})
export class ProjectInsightsService {
  private _property: BehaviorSubject<Property | null>;
  private _project: Project;
  private _properties: Property[];
  private _accountId: string;
  private _compsets: BehaviorSubject<any | null>;
  _compset: BehaviorSubject<any | null>;

  constructor(private _httpClient: HttpClient) {
    this._property = new BehaviorSubject(null);
    this._compsets = new BehaviorSubject(null);
    this._compset = new BehaviorSubject(null);
  }

  get property$(): Observable<Property> {
    return this._property.asObservable();
  }

  setProperty(id: number): Observable<Property> {
    const index = this._properties.findIndex((item) => item.id === id);
    const property = this._properties[index];
    this._property.next(property);
    return of(property);
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  set project(val: Project) {
    this._project = val;
    this._properties = this._project.properties;
  }

  get project() {
    return this._project;
  }

  get properties() {
    return this._properties;
  }

  setCompsets(compsets) {
    this._compsets.next(compsets);
  }

  get compsets$(): Observable<any> {
    return this._compsets.asObservable();
  }

  get compset$(): Observable<any> {
    return this._compset.asObservable();
  }

  getCompsets(): Observable<any> {
    return this._httpClient.get<any>(`projects/${this.project.id}/compsets`).pipe(
      map((response) => {
        this.setCompsets(response.compsets);
        return response;
      }),
    );
  }

  saveCompset(compset: any): Observable<any> {
    compset = { ...compset, project_id: this.project.id };
    if (compset.id) {
      return this.compsets$.pipe(
        take(1),
        switchMap((compsets) =>
          this._httpClient.put<any>(`projects/${this.project.id}/compsets/${compset.id}`, { compset }).pipe(
            map((response) => {
              let compset = response.compset;
              const index = compsets.findIndex((item) => item.id == compset.id);

              // Delete the label
              compsets[index] = compset;

              // Update the labels
              this._compsets.next(compsets);

              // Return the deleted status
              return response.compset;
            }),
          ),
        ),
      );
    } else {
      return this.compsets$.pipe(
        take(1),
        switchMap((compsets) =>
          this._httpClient.post<any>(`projects/${this.project.id}/compsets`, { compset }).pipe(
            map((response) => {
              if (compsets) {
                // Update the contacts with the new contact
                this._compsets.next([response.compset, ...compsets]);
              }

              // Return the new contact
              return response.compset;
            }),
          ),
        ),
      );
    }
  }

  exportCompset(compsetId: any): Observable<any> {
    return this._httpClient.get<any>(`projects/${this.project.id}/compsets/${compsetId}/export`).pipe(
      map((response) => {
        this.setCompsets(response);
        return response;
      }),
    );
  }

  getCompset(id: any): Observable<any> {
    return this._httpClient.get<any>(`projects/${this.project.id}/compsets/${id}`).pipe(
      map((response: any) => {
        this._compset.next(response.compset);
      }),
    );
  }

  deleteCompset(id: number): Observable<any> {
    return this.compsets$.pipe(
      take(1),
      switchMap((compsets) =>
        this._httpClient.delete<any>(`projects/${this.project.id}/compsets/${id}`).pipe(
          map((response) => {
            // Find the index of the deleted label within the labels
            const index = compsets.findIndex((item) => item.id == id);

            // Delete the label
            compsets.splice(index, 1);

            // Update the labels
            this._compsets.next(compsets);

            // Return the deleted status
            return response.compset;
          }),
        ),
      ),
    );
  }
}
